import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { find as _find } from 'lodash';

import SimplePrompt from 'components/layout/SimplePrompt';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


function CmsElementRow(props) {
  const { editMode, index, layout, children, scale, showElementSettings, removeElement } = props;

  // UI States
  const [mouseOver, setMouseOver] = useState(false);
  const [rowMenuMarginLeft, setRowMenuMarginLeft] = useState(0);
  const containerRef = useRef(null);

  // Hook for the basic CMS logic (replaces AbstractCmsElement)
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getResponsiveClasses,
    getPaddingClasses,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index,
    disabled: !editMode,
  });

  // Calculate dynamic menu shift (prevents overlap with grid menu)
  useEffect(() => {
    if (editMode && mouseOver && containerRef.current) {
      const rowMenu = containerRef.current.querySelector('.ms-row-menu');
      if (rowMenu) {
        const rowMenuRect = rowMenu.getBoundingClientRect();
        let gridContainer = containerRef.current;

        // Search the parent grid
        while (gridContainer.parentElement && !gridContainer.classList.contains('ms-grid')) {
          gridContainer = gridContainer.parentElement;
        }

        if (gridContainer && gridContainer.classList.contains('ms-grid')) {
          const gridMenu = gridContainer.querySelector('.ms-grid-menu');
          if (gridMenu) {
            const gridMenuRect = gridMenu.getBoundingClientRect();
            // When menus overlap
            if (
              gridMenuRect.left + gridMenuRect.width > rowMenuRect.left &&
              gridMenuRect.top + gridMenuRect.height > rowMenuRect.top
            ) {
              const overlap = gridMenuRect.width - (rowMenuRect.left - gridMenuRect.left) + 10;
              setRowMenuMarginLeft(overlap * (1 / scale));
            } else {
              setRowMenuMarginLeft(0);
            }
          }
        }
      }
    } else {
      setRowMenuMarginLeft(0);
    }
  }, [mouseOver, editMode, scale]);

  // Column parameters, helper functions
  const getColDef = (col) => {
    const getColParamValue = (column, name, def) => {
      const param = _find(column.props?.params, ['name', name]);
      return param?.value ?? def;
    };

    const xs = parseInt(getColParamValue(col, 'xs', 12), 10);
    const sm = parseInt(getColParamValue(col, 'sm', xs), 10);
    const md = parseInt(getColParamValue(col, 'md', sm), 10);
    const lg = parseInt(getColParamValue(col, 'lg', md), 10);

    const hiddenPhone = !editMode && getColParamValue(col, 'hiddenPhone', 'no') === 'yes';
    const hiddenTablet = !editMode && getColParamValue(col, 'hiddenTablet', 'no') === 'yes';
    const hiddenDesktop = !editMode && getColParamValue(col, 'hiddenDesktop', 'no') === 'yes';

    return {
      xs: hiddenPhone ? 0 : xs,
      sm: hiddenTablet ? 0 : sm,
      md: hiddenTablet ? 0 : md,
      lg: hiddenDesktop ? 0 : lg,
    };
  };

  // Styles for dnd-kit
  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10000 : 'auto',
    touchAction: 'none', // Prevents the phone from scrolling while dragging
  };

  // CSS classes for column alignment
  let equalHeightColClass = '';
  const colSameHeight =
    (layout.breakpointIndex === 0 && getParamValue('colSameHeightPhone', 'no') === 'yes') ||
    (layout.breakpointIndex <= 2 && getParamValue('colSameHeightTablet', 'no') === 'yes') ||
    (layout.breakpointIndex === 3 && getParamValue('colSameHeightDesktop', 'no') === 'yes');

  if (colSameHeight) equalHeightColClass = 'row-eq-height ';

  const parallaxData = getParallaxData();
  let colCounter = 0;

  const innerContent = (
    <div
      ref={containerRef}
      id={getMainId()}
      className={`
        ms-row row
        ${shown ? 'elem-shown ' : 'elem-hidden '}
        ${equalHeightColClass}
        ${getResponsiveClasses()}
        ${getParamValue('isCondensed', 'no') === 'yes' ? 'condensed ' : ''}
        ${cssClasses}
        ${getPaddingClasses(15)}
        ${isDragging ? 'ms-row-dragging' : ''}
      `}
      style={getBackgroundStyle()}
      onMouseEnter={() => editMode && setMouseOver(true)}
      onMouseLeave={() => editMode && setMouseOver(false)}
    >
      {elementStyleTag}

      {/* Row Edit Menu */}
      {mouseOver && editMode && (
        <>
          <span className="ms-row-outline ms-row-outline-top" />
          <span className="ms-row-outline ms-row-outline-right" />
          <span className="ms-row-outline ms-row-outline-bottom" />
          <span className="ms-row-outline ms-row-outline-left" />
          <div
            className="ms-row-menu"
            style={{
              transform: `scale(${1 / scale})`,
              transformOrigin: 'top left',
              marginLeft: `${rowMenuMarginLeft}px`,
            }}
          >
            <button className="btn btn-default cursor-move" type="button" {...attributes} {...listeners}>
              <i className="fal fa-arrows" />
            </button>
            <Button onClick={(e) => showElementSettings(e, index)}>
              <i className="fal fa-cog" />
            </Button>
            <SimplePrompt
              onSuccess={() => removeElement(index)}
              placement="bottom"
              variant="default"
              showLabel={false}
            />
          </div>
        </>
      )}

      {/* Render Columns */}
      {children &&
        React.Children.map(children, (col, i) => {
          if (!col) return null;
          const colDef = getColDef(col);
          const elements = [React.cloneElement(col, { key: `col-${i}` })];

          colCounter += colDef[layout.breakpoint] || 0;
          if (colCounter % 12 === 0) {
            elements.push(<div className="clearfix" key={`clearfix-${i}`} />);
          }
          return elements;
        })}

      {getAppendHtml()}
    </div>
  );

  return (
    <div ref={setNodeRef} style={dndStyle}>
      {/* We disable ScrollTrigger/Plx during dragging for better performance */}
      {isDragging ? (
        innerContent
      ) : (
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>{innerContent}</Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      )}
    </div>
  );
}

export default CmsElementRow;
